/**
 * Tooltip placement relative to its anchor. Pass via `data-tooltip-placement`.
 * Use `'auto'` to let the engine pick based on available viewport space.
 *
 * @remarks `'auto'` writes the resolved placement back to `data-tooltip-placement`
 * on the tooltip element, useful for CSS arrow orientation.
 */
type TooltipPlacement =
  | 'auto'
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end';

/**
 * Visual severity variant for the tooltip. Applied via `data-tooltip-severity`
 * as a CSS styling hook on {@link TooltipPopover}.
 */
type TooltipSeverity = 'neutral' | 'success' | 'info' | 'warning' | 'danger';

/**
 * Singleton static class managing a single shared tooltip element for the page.
 * Designed for use with a body-level delegated event engine.
 *
 * **Setup:**
 * 1. Call {@link TooltipPopover.init} once at startup.
 * 2. Wire {@link TooltipPopover.handleToggle} to the `show-tooltip` action.
 * 3. Add `data-action="show-tooltip"` and `data-tooltip-content="…"` to triggers.
 *
 * **Trigger attributes:**
 * - `data-tooltip-content` (required): Plain text; HTML is not interpreted.
 * - `data-tooltip-placement` (optional): {@link TooltipPlacement}. Defaults to `'auto'`.
 * - `data-tooltip-severity` (optional): {@link TooltipSeverity}. Defaults to `'neutral'`.
 *
 * @remarks
 * All methods and state are static — never instantiate this class.
 * `init` registers no duplicate guards; call it exactly once.
 * CSS spacing is controlled via `--tooltip-popover-gap` and
 * `--tooltip-popover-viewport-margin` on `.tooltip-popover`.
 *
 * @example
 * ```ts
 * globalEvent({
 *   type: 'click',
 *   event: { action: 'show-tooltip', callback: TooltipPopover.handleToggle }
 * });
 * TooltipPopover.init();
 * ```
 */
export class TooltipPopover {
  /** Data-action value used to identify tooltip triggers handled by this component. */
  private static readonly ACTION = 'show-tooltip';

  /** Fixed DOM id for the single shared (global) tooltip/popover element. */
  private static readonly TOOLTIP_ID = 'global-tooltip-popover';

  /** The shared tooltip/popover element managed by this class (created lazily). */
  private static tooltipEl: HTMLDivElement | null = null;

  /** The element the tooltip is currently anchored to, if any. */
  private static anchorEl: HTMLElement | null = null;

  /** Tracks whether the tooltip is currently shown. */
  private static isOpen = false;

  /**
   * Toggles the tooltip for the nearest `data-action="show-tooltip"` ancestor
   * of the event target. Same anchor closes; different anchor repositions in place.
   * No-op if no matching ancestor or `data-tooltip-content` is empty.
   *
   * @param e - Any `Event` with a valid `target`; typically a click `MouseEvent`.
   *
   * @example
   * ```ts
   * globalEvent({
   *   type: 'click',
   *   event: { action: 'show-tooltip', callback: TooltipPopover.handleToggle }
   * });
   * ```
   */
  public static handleToggle(e: Event): void {
    const caller = TooltipPopover.resolveActionCaller(e, TooltipPopover.ACTION);
    if (!caller) {
      return;
    }

    // Toggle behavior: clicking the same caller closes it
    if (TooltipPopover.isOpen && TooltipPopover.anchorEl === caller) {
      TooltipPopover.close();
      return;
    }

    const content = caller.dataset.tooltipContent ?? '';
    if (!content.trim()) {
      // If no content, do nothing (or close if open)
      TooltipPopover.close();
      return;
    }

    const placement =
      (caller.dataset.tooltipPlacement as TooltipPlacement) ?? 'auto';
    const severity = TooltipPopover.parseSeverity(
      caller.dataset.tooltipSeverity
    );

    TooltipPopover.open(caller, content, { placement, severity });
  }

  /**
   * Registers global listeners for tooltip dismissal and repositioning.
   * Call once at startup — no duplicate guard, so avoid calling more than once.
   *
   * @remarks
   * Listens for `Escape` and outside clicks to close, and `resize`/`scroll` to
   * reposition. Outside-click and scroll use capture phase to run before any
   * `stopPropagation` handlers.
   */
  public static init(): void {
    // Close on Escape
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') {
        TooltipPopover.close();
      }
    });

    // Close on outside click (capture phase so it runs early)
    document.addEventListener(
      'click',
      (ev) => {
        if (!TooltipPopover.isOpen || !TooltipPopover.tooltipEl) {
          return;
        }

        const el = ev.target instanceof Element ? ev.target : null;
        const clickedInsideTooltip =
          el !== null && TooltipPopover.tooltipEl.contains(el);
        const clickedAnchor =
          el !== null &&
          TooltipPopover.anchorEl !== null &&
          TooltipPopover.anchorEl.contains(el);

        if (!clickedInsideTooltip && !clickedAnchor) {
          TooltipPopover.close();
        }
      },
      true
    );

    // Reposition on resize/scroll
    window.addEventListener('resize', () => TooltipPopover.reposition());
    window.addEventListener('scroll', () => TooltipPopover.reposition(), true);
  }

  /**
   * Opens the tooltip anchored to `anchor`, creating the tooltip element if
   * needed. Repopulates and repositions in place if already open.
   *
   * @param anchor - Must be in the live DOM. Receives a generated `id` and
   *   `aria-describedby` if not already set.
   * @param content - Plain text only; assigned via `textContent`.
   * @param opts.placement - Pass `'auto'` to let the engine decide.
   * @param opts.severity - Applied as `data-tooltip-severity` for CSS targeting.
   */
  private static open(
    anchor: HTMLElement,
    content: string,
    opts: { placement: TooltipPlacement; severity: TooltipSeverity }
  ): void {
    const tooltip = TooltipPopover.ensureTooltipElement();

    const body = tooltip.querySelector<HTMLElement>('[data-tooltip-body]');
    if (body) {
      body.textContent = content;
    }

    tooltip.setAttribute('data-tooltip-severity', opts.severity);

    if (!anchor.id) {
      anchor.id = TooltipPopover.autoId('tt-anchor');
    }
    tooltip.setAttribute('data-anchor-id', anchor.id);

    if (tooltip.id && !anchor.hasAttribute('aria-describedby')) {
      anchor.setAttribute('aria-describedby', tooltip.id);
    }

    // Show.
    tooltip.hidden = false;
    tooltip.setAttribute('data-open', 'true');
    TooltipPopover.isOpen = true;
    TooltipPopover.anchorEl = anchor;

    // Place.
    TooltipPopover.positionTooltip(anchor, tooltip, opts.placement);
  }

  /**
   * Closes the tooltip and cleans up DOM and ARIA state. Safe to call at any time.
   *
   * @remarks
   * The tooltip element is retained in the DOM and reused on the next `open` call.
   * `aria-describedby` is only removed from the anchor if it still points to this
   * tooltip's `id`.
   */
  public static close(): void {
    if (!TooltipPopover.tooltipEl) {
      return;
    }

    TooltipPopover.tooltipEl.hidden = true;
    TooltipPopover.tooltipEl.removeAttribute('data-open');
    TooltipPopover.isOpen = false;

    // Clean up aria-describedby on previous anchor (best-effort)
    if (TooltipPopover.anchorEl) {
      const describedby =
        TooltipPopover.anchorEl.getAttribute('aria-describedby');
      if (describedby === TooltipPopover.tooltipEl.id) {
        TooltipPopover.anchorEl.removeAttribute('aria-describedby');
      }
    }

    TooltipPopover.anchorEl = null;
  }

  /**
   * Repositions the tooltip relative to its current anchor. No-op if not open.
   * Called automatically by the `resize` and `scroll` listeners in `init`.
   *
   * @remarks
   * No debouncing or throttling — consider wrapping `init`'s listeners at the
   * call site if scroll or resize events fire at high frequency.
   */
  private static reposition(): void {
    if (
      !TooltipPopover.isOpen ||
      !TooltipPopover.tooltipEl ||
      !TooltipPopover.anchorEl
    ) {
      return;
    }

    const placement =
      (TooltipPopover.anchorEl.dataset.tooltipPlacement as TooltipPlacement) ??
      'auto';
    TooltipPopover.positionTooltip(
      TooltipPopover.anchorEl,
      TooltipPopover.tooltipEl,
      placement
    );
  }

  /**
   * Computes and applies absolute document coordinates for `tooltip` relative to
   * `anchor`, with viewport clamping and overflow flipping.
   *
   * @param anchor - The element to anchor to.
   * @param tooltip - Must be visible at call time so its dimensions can be measured.
   * @param placement - Pass `'auto'` to let the engine pick based on available space.
   *
   * @remarks
   * Resolves `'auto'` by comparing space above/below and checking horizontal
   * overflow. Clamps horizontally and flips vertically if the tooltip would
   * overflow the viewport. Spacing is read from `--tooltip-popover-gap` and
   * `--tooltip-popover-viewport-margin` on the tooltip element.
   * Resolved placement is written back as `data-tooltip-placement`.
   */
  private static positionTooltip(
    anchor: HTMLElement,
    tooltip: HTMLDivElement,
    placement: TooltipPlacement
  ): void {
    // Ensure tooltip is measurable
    tooltip.style.left = '0px';
    tooltip.style.top = '0px';

    // Read spacing from CSS (themeable / responsive via media queries)
    const gap = TooltipPopover.getCssPx(tooltip, '--tooltip-popover-gap', 8);
    const margin = TooltipPopover.getCssPx(
      tooltip,
      '--tooltip-popover-viewport-margin',
      gap
    );

    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    // Default: bottom-start
    let desired: TooltipPlacement = placement;

    if (desired === 'auto') {
      // Choose bottom unless it would overflow vertically a lot
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      const preferTop =
        spaceBelow < tooltipRect.height + gap && spaceAbove > spaceBelow;

      // Choose start/end based on horizontal overflow
      const wouldOverflowRightIfStart =
        anchorRect.left + tooltipRect.width > window.innerWidth - margin;
      const wouldOverflowLeftIfEnd =
        anchorRect.right - tooltipRect.width < margin;
      const preferEnd = wouldOverflowRightIfStart && !wouldOverflowLeftIfEnd;

      if (preferTop) {
        desired = preferEnd ? 'top-end' : 'top-start';
      } else {
        desired = preferEnd ? 'bottom-end' : 'bottom-start';
      }
    }

    // Compute left/top in viewport coords
    let left = 0;
    let top = 0;

    const clamp = (val: number, min: number, max: number) =>
      Math.min(Math.max(val, min), max);

    const minLeft = margin;
    const maxLeft = window.innerWidth - tooltipRect.width - margin;

    const placeBottom = desired.startsWith('bottom');
    const placeTop = desired.startsWith('top');
    const alignStart = desired.endsWith('start');
    const alignEnd = desired.endsWith('end');

    if (alignStart) {
      left = anchorRect.left;
    } else if (alignEnd) {
      left = anchorRect.right - tooltipRect.width;
    }

    if (placeBottom) {
      top = anchorRect.bottom + gap;
    } else if (placeTop) {
      top = anchorRect.top - tooltipRect.height - gap;
    }

    // Clamp into viewport.
    left = clamp(left, minLeft, maxLeft);

    // If top overflows, try flipping.
    if (top < margin) {
      const flippedTop = anchorRect.bottom + gap;
      if (flippedTop + tooltipRect.height <= window.innerHeight - margin) {
        top = flippedTop;
      } else {
        top = margin;
      }
    } else if (top + tooltipRect.height > window.innerHeight - margin) {
      const flippedTop = anchorRect.top - tooltipRect.height - gap;
      if (flippedTop >= margin) {
        top = flippedTop;
      } else {
        top = window.innerHeight - tooltipRect.height - margin;
      }
    }

    // Convert to document coords.
    const docLeft = left + window.scrollX;
    const docTop = top + window.scrollY;

    tooltip.style.left = `${docLeft}px`;
    tooltip.style.top = `${docTop}px`;

    // Store placement for CSS arrow positioning if you add one.
    tooltip.setAttribute('data-tooltip-placement', desired);
  }

  /**
   * Returns the singleton tooltip element, creating and appending it to
   * `document.body` if needed. Checks the cached reference first, then the DOM
   * by `id`, then creates a new element.
   *
   * @returns The singleton tooltip `HTMLDivElement`.
   *
   * @remarks
   * The created element has `role="tooltip"`, `aria-live="polite"`, and `hidden`
   * set. Visibility is managed by `open` and `close` — do not toggle `hidden` directly.
   */
  private static ensureTooltipElement(): HTMLDivElement {
    if (
      TooltipPopover.tooltipEl &&
      document.body.contains(TooltipPopover.tooltipEl)
    ) {
      return TooltipPopover.tooltipEl;
    }

    const existing = document.getElementById(
      TooltipPopover.TOOLTIP_ID
    ) as HTMLDivElement | null;
    if (existing) {
      TooltipPopover.tooltipEl = existing;
      return existing;
    }

    const tooltip = document.createElement('div');
    tooltip.id = TooltipPopover.TOOLTIP_ID;
    tooltip.hidden = true;

    // Semantics
    tooltip.setAttribute('role', 'tooltip');
    tooltip.setAttribute('aria-live', 'polite');

    // Base class + minimal structure for styling + arrow
    tooltip.className = 'tooltip-popover';
    tooltip.innerHTML = `
      <div class="tooltip-popover__arrow" aria-hidden="true"></div>
      <div class="tooltip-popover__body" data-tooltip-body></div>
    `;

    document.body.appendChild(tooltip);

    TooltipPopover.tooltipEl = tooltip;
    return tooltip;
  }

  /**
   * Resolves the nearest ancestor of `e.target` (inclusive) with a matching
   * `data-action` attribute. Supports delegated event handling where clicks may
   * originate from child elements.
   *
   * @param e - Only `e.target` is inspected; the event is not consumed.
   * @param action - The `data-action` value to match, e.g. `'show-tooltip'`.
   * @returns The nearest matching `HTMLElement`, or `null` if none is found.
   */
  private static resolveActionCaller(
    e: Event,
    action: string
  ): HTMLElement | null {
    if (!(e.target instanceof Element)) {
      return null;
    }
    return e.target.closest<HTMLElement>(`[data-action="${action}"]`);
  }

  /**
   * Generates a pseudo-random DOM id of the form `<prefix>-<8-char base-36>`.
   *
   * @param prefix - Namespace prefix, e.g. `'tooltip'`.
   * @returns A string suitable for use as an element `id` attribute.
   *
   * @remarks Not cryptographically secure; for in-page uniqueness only.
   */
  private static autoId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Parses and normalizes a raw severity string to a {@link TooltipSeverity}.
   * Falls back to `'neutral'` for unknown, empty, or missing values.
   *
   * @param value - Typically read from `data-tooltip-severity`.
   * @returns A normalized {@link TooltipSeverity} value.
   */
  private static parseSeverity(
    value: string | undefined | null
  ): TooltipSeverity {
    const normalized = value?.trim().toLowerCase();

    switch (normalized) {
      case 'success':
      case 'info':
      case 'warning':
      case 'danger':
      case 'neutral':
        return normalized;
      default:
        return 'neutral';
    }
  }

  /**
   * Reads a CSS custom property from `elementToQuery`'s computed style and returns it as a
   * number. Falls back to `fallbackPx` if unset or non-numeric.
   *
   * @param elementToQuery - Element to query; use the tooltip element to capture scoped overrides.
   * @param name - Custom property name including `--`, e.g. `'--tooltip-popover-gap'`.
   * @param fallbackPx - Returned if the property is unset or non-numeric.
   * @returns Parsed pixel value, or `fallbackPx`.
   *
   * @remarks Property must be authored in `px`; other units parse numerically without conversion.
   */
  private static getCssPx(
    elementToQuery: Element,
    name: string,
    fallbackPx: number
  ): number {
    const raw = getComputedStyle(elementToQuery).getPropertyValue(name).trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : fallbackPx;
  }
}
